import { useLocation, useNavigate } from '@/lib/router-compat';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';

// Get the base path without language prefix
export const getBasePath = (pathname: string): string => {
  if (pathname.startsWith('/en/')) {
    return pathname.slice(3) || '/';
  }
  if (pathname === '/en') {
    return '/';
  }
  return pathname;
};

// Get language from URL
export const getLanguageFromUrl = (pathname: string): string => {
  if (pathname.startsWith('/en/') || pathname === '/en') {
    return 'en';
  }
  return 'de';
};

// Get localized path
export const getLocalizedPath = (basePath: string, language: string): string => {
  if (language === 'en') {
    return basePath === '/' ? '/en' : `/en${basePath}`;
  }
  return basePath;
};

export const useLanguageRouting = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  const currentLanguage = getLanguageFromUrl(location.pathname);
  const basePath = getBasePath(location.pathname);

  // Sync i18n language with URL on mount and URL change
  useEffect(() => {
    if (i18n.language !== currentLanguage) {
      i18n.changeLanguage(currentLanguage);
    }
  }, [currentLanguage, i18n]);

  // Switch language and navigate to new URL
  const switchLanguage = (newLanguage: string) => {
    const newPath = getLocalizedPath(basePath, newLanguage);
    navigate(newPath + location.search + location.hash);
  };

  // Get localized link for navigation
  const getLocalizedLink = (path: string): string => {
    return getLocalizedPath(path, currentLanguage);
  };

  return {
    currentLanguage,
    basePath,
    switchLanguage,
    getLocalizedLink,
    isEnglish: currentLanguage === 'en',
    isGerman: currentLanguage === 'de',
  };
};

export default useLanguageRouting;
